import { useRef } from 'react';
import * as React from 'react';
import { motion, useInView } from 'framer-motion';
import styled, { css } from 'styled-components';

import { ReactComponent as MagicSpinnerBkg } from '../../images/illustrations/illustration-spinner-bkg.svg';
import { spin } from '../../utils/theme/themes';
import { GradientHeader } from '../design-system';
import { ToutWrapper } from '../design-system/ToutWrapper';

import { ActionableApps } from './product-illustrations/ActionableApps';
import { DataExploration } from './product-illustrations/DataExploration';
import MagicTout from './product-illustrations/MagicTout';
import { TeamWork } from './product-illustrations/TeamWork';
import { ProductOverviewSection } from './ProductOverviewSection';

const StyledToutWrapper = styled(ToutWrapper)`
  align-items: center;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 35px 0 30px;
  width: 100%;
  border-top: 1px dashed ${({ theme }) => theme.borderColor.MUTED};
  border-bottom: 1px dashed ${({ theme }) => theme.borderColor.MUTED};
`;

const Inner = styled.div`
  display: flex;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
  z-index: 1;
  position: relative;
  width: 100%;
`;

const StyledHeading = styled(GradientHeader).attrs({
  $purple: true,
  renderAs: 'h1'
})`
  && {
    margin-bottom: 0;
    padding: 0 20px;
    max-width: 800px;
  }
`;

const SectionsList = styled.div`
  display: grid;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const FixedBackground = styled(motion.div)<{
  $isVisible: boolean;
}>`
  position: fixed;
  z-index: 0;
  top: 0;
  opacity: 0;
  pointer-events: none;
  display: flex;
  width: 100vw;
  height: 100vh;
  align-items: center;
  justify-content: center;
  transition: opacity 0.5s ${({ theme }) => theme.animation.EASING};

  svg {
    scale: 1.35;
    flex: none;
    animation: ${spin} 120s linear infinite;

    @media (max-width: ${({ theme }) => theme.mediaQuery.LARGE}) {
      scale: 2.2;
    }

    @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
      scale: 3;
    }
  }

  ${({ $isVisible }) =>
    $isVisible &&
    css`
      opacity: 0.8;
    `}
`;

const CONTENT = [
  <DataExploration />,
  <MagicTout />,
  <TeamWork />,
  <ActionableApps />
];

export const ProductOverviewTout: React.ComponentType = React.memo(() => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  const hasHitHalfWay = useInView(wrapperRef, {
    margin: '-40% 0px -90% 0px'
  });

  return (
    <StyledToutWrapper ref={wrapperRef}>
      <Top>
        <StyledHeading renderAs="h1">
          <span>All your workflows, </span>
          <span>in one workspace</span>
        </StyledHeading>
      </Top>
      <Inner>
        <SectionsList>
          {CONTENT.map((item, index) => (
            <ProductOverviewSection
              key={index}
              className={index === 1 ? 'full-width' : ''}
              item={item}
            />
          ))}
        </SectionsList>
      </Inner>
      <FixedBackground $isVisible={hasHitHalfWay}>
        <MagicSpinnerBkg />
      </FixedBackground>
    </StyledToutWrapper>
  );
});
